import type { Warehouse, WarehouseCreate } from '@erp/shared';
import { Ban, Pencil, RotateCcw, Warehouse as WarehouseIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

export function WarehousesPage(): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('warehouses.write');
  const [items, setItems] = useState<Warehouse[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Warehouse[] }>('/warehouses');
    setItems(r.data.items);
  };
  useEffect(() => { void reload(); }, []);

  if (!items) {
    return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;
  }

  const onDeactivate = async (w: Warehouse): Promise<void> => {
    if (!(await confirmDialog({ message: t('warehouses.actions.confirmDeactivate', { code: w.code }), tone: 'danger' }))) return;
    try { await api().delete(`/warehouses/${w.id}`); await reload(); }
    catch { toast.error(tc('errors:server')); }
  };

  const onReactivate = async (w: Warehouse): Promise<void> => {
    if (!(await confirmDialog({ message: t('warehouses.actions.confirmReactivate', { code: w.code }) }))) return;
    try { await api().patch(`/warehouses/${w.id}`, { isActive: true }); await reload(); }
    catch { toast.error(tc('errors:server')); }
  };

  const openEdit = (w: Warehouse): void => {
    setShowCreate(false);
    setEditing(w);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: DataTableColumn<Warehouse>[] = [
    {
      id: 'code',
      header: t('warehouses.columns.code'),
      sortValue: (w) => w.code,
      searchValue: (w) => w.code,
      cellClassName: 'font-mono text-xs',
      cell: (w) => w.code,
    },
    {
      id: 'name',
      header: t('warehouses.columns.name'),
      sortValue: (w) => w.name,
      searchValue: (w) => w.name,
      cellClassName: 'font-medium',
      cell: (w) => w.name,
    },
    {
      id: 'status',
      header: t('warehouses.columns.status'),
      sortValue: (w) => (w.isActive ? 1 : 0),
      searchValue: (w) => t(`status.${w.isActive ? 'active' : 'inactive'}`),
      cell: (w) => (
        <Badge variant={w.isActive ? 'success' : 'secondary'}>
          {t(`status.${w.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-32',
            cell: (w: Warehouse) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(w)}
                  aria-label={`${t('warehouses.actions.edit')} ${w.code}`}
                  title={t('warehouses.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {w.isActive ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeactivate(w)}
                    aria-label={`${t('warehouses.actions.deactivate')} ${w.code}`}
                    title={t('warehouses.actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReactivate(w)}
                    aria-label={`${t('warehouses.actions.reactivate')} ${w.code}`}
                    title={t('warehouses.actions.reactivate')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('warehouses.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('warehouses.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('warehouses.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <WarehouseForm
          mode="create"
          onCancel={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await reload(); }}
        />
      )}

      {editing && (
        <WarehouseForm
          mode="edit"
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}

      {items.length === 0 && !showCreate && !editing ? (
        <EmptyState
          icon={<WarehouseIcon className="h-8 w-8" aria-hidden />}
          title={t('warehouses.empty')}
          description={t('warehouses.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('warehouses.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(w) => w.id} />
        </Card>
      )}
    </div>
  );
}

interface WarehouseFormProps {
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'; populates the form and supplies the id for PATCH. */
  initial?: Warehouse;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function WarehouseForm({ mode, initial, onCancel, onSaved }: WarehouseFormProps): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const isEdit = mode === 'edit';
  const [form, setForm] = useState<WarehouseCreate>(() =>
    initial ? { code: initial.code, name: initial.name } : { code: '', name: '' },
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit && initial) {
        // Code is the unique identifier and is read-only in edit mode —
        // WarehouseUpdateSchema doesn't accept it. Address editing isn't
        // surfaced on the list page; deeper edits can follow when there's
        // an address subform to reuse.
        await api().patch(`/warehouses/${initial.id}`, { name: form.name.trim() });
      } else {
        await api().post('/warehouses', { code: form.code.trim(), name: form.name.trim() });
      }
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'conflict') setError(t('errors.duplicateCode'));
      else if (code === 'unprocessable') setError(t('warehouses.errors.unprocessable'));
      else setError(tc('errors:server'));
    } finally { setSaving(false); }
  };

  const title = isEdit && initial
    ? t('warehouses.edit.title', { code: initial.code, name: initial.name })
    : t('warehouses.create.title');

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{title}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="w-code" label={t('warehouses.create.code')} required>
          <Input
            id="w-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            autoFocus={!isEdit}
            readOnly={isEdit}
            aria-readonly={isEdit}
            className={isEdit ? 'cursor-not-allowed bg-bg-muted text-fg-muted' : undefined}
          />
        </FormField>
        <FormField id="w-name" label={t('warehouses.create.name')} required>
          <Input
            id="w-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus={isEdit}
          />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {isEdit ? t('warehouses.edit.submit') : t('warehouses.create.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {isEdit ? t('warehouses.edit.cancel') : t('warehouses.create.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
